const en = {
  common: {
    loading: "Loading...",
    error: "Error",
    save: "Save",
    cancel: "Cancel",
    delete: "Delete",
    edit: "Edit",
    close: "Close",
    search: "Search",
    noResults: "No results found",
    confirm: "Confirm",
    run: "Run",
    stop: "Stop",
    clear: "Clear",
    refresh: "Refresh",
    add: "Add",
    all: "All",
    duplicate: "Duplicate",
    expand: "Expand",
    collapse: "Collapse",
    copy: "copy",
    running: "Running...",
    stopping: "Stopping...",
    clearing: "Clearing...",
    generating: "Generating...",
    summarizing: "Summarizing...",
    noContent: "No content",
  },
  nav: {
    patients: "Clients",
    groupCaseload: "Caseload",
    groupSession: "Session",
    groupPatient: "Client",
    groupLibrary: "Library",
    theme: "Theme",
    language: "Language",
    billing: "Billing",
    settings: "Settings",
    logout: "Logout",
  },
  tabs: {
    session: "Session",
    prepare: "Prepare",
    canvas: "Toolkit",
    assistant: "Assistant",
    journey: "Trends",
    goals: "Homework",
    calendar: "Calendar",
    histories: "Histories",
    assessments: "Tests",
    cards: "Prompts",
    prompts: "Prompts",
    library: "Library",
  },
  library: {
    title: "Reference library",
    hint: "Upload papers, protocols, and methodology documents (PDF, TXT, MD, max 25 MB). The assistant can search them and cite excerpts by title and page.",
    uploadButton: "Upload document",
    uploading: "Uploading…",
    loading: "Loading…",
    empty: "No documents yet. Upload a paper to let the assistant cite it.",
    pages: "pages",
    statusProcessing: "Processing…",
    statusReady: "Ready",
    statusError: "Error",
    download: "Download",
    delete: "Delete",
    cancel: "Cancel",
    deleteConfirmTitle: "Delete document?",
    deleteConfirmBody: "\"{{title}}\" and its search index will be permanently removed.",
    listFailed: "Could not load the library.",
    uploadFailed: "Upload failed.",
    deleteFailed: "Delete failed.",
    downloadFailed: "Download failed.",
    stuckMessage: "Processing appears stuck — delete the document and re-upload.",
  },
  session: {
    title: "Session Notes",
    noSession: "No session selected",
    selectPatient: "Select a client to view their sessions",
    timeline: "Timeline",
    insights: "Insights",
    transcript: "Transcript",
    liveTranscript: "Live transcript",
    summary: "Summary",
    tags: "Tags",
    notes: "Notes",
    sessionDate: "Session date",
    // Toolbar actions
    startRecording: "Start recording",
    stopRecording: "Stop recording",
    uploadVoice: "Upload voice",
    recordOnline: "Online",
    startOnlineRecording: "Record online session",
    onlineNoAudioTrack: "No audio was shared. Pick a browser tab (or entire screen on Windows) and tick “Also share audio”, then try again.",
    onlineMicDenied: "Microphone access was denied. The online recording was not started.",
    onlineStartFailed: "Could not start the online recording.",
    chunkUploadFailed: "Part of the recording could not be uploaded. Check your connection — some audio may be missing from the transcript.",
    micDenied: "Microphone access was denied. The recording was not started.",
    micUnavailable: "No usable microphone was found. The recording was not started.",
    recordStartFailed: "Could not start the recording.",
    recordingInterrupted: "Recording stopped unexpectedly — please start it again.",
    recordingLabel: "Recording",
    recStripHint: "Recording in progress — click to open the session panel",
    chunksUploaded_one: "{{count}} chunk uploaded",
    chunksUploaded_other: "{{count}} chunks uploaded",
    chunksLost_one: "{{count}} lost",
    chunksLost_other: "{{count}} lost",
    showTranscript: "Show transcript",
    hideTranscript: "Hide transcript",
    showNotes: "Show notes",
    clearTranscript: "Clear transcript",
    saveSession: "Save session",
    deleteSession: "Delete session",
    additionalContext: "Additional session context",
    // Confirm dialogs
    clearTranscriptTitle: "Clear Transcript",
    clearTranscriptMessage: "Clear transcript? This will clear the transcript on the server for this session.",
    deleteSessionTitle: "Delete Session",
    deleteSessionMessage: "Delete this session? This action cannot be undone.",
    // Summary card
    generateSummary: "Generate summary",
    sentToWorkspace: "Sent to workspace",
    summaryEmptyState: "Record a session or type notes here.\nUse the sparkle button to auto-summarize.",
    // Transcript
    transcriptEmptyState: "Start recording to capture the conversation.\nTap any message to pin important moments.",
    pinQuote: "Pin quote",
    unpin: "Unpin",
    // Recording indicator
    recording: "REC",
    idle: "IDLE",
    // Replay
    replaySession: "Replay session",
    exitReplay: "Exit replay",
    replaySpeed: "Playback speed",
    record: "Record",
    replayBtn: "Replay",
    replay: "REPLAY",
    // Upload processing
    uploadingFile: "Uploading file...",
    processingAudio: "Processing audio... transcript will appear shortly",
    processingComplete: "Processing complete",
    uploadFailed: "Upload failed",
    processingFailed: "Audio processing failed",
    processingTimeout: "Transcription did not complete — the processing job appears to have failed.",
    transcriptConnectionLost: "Lost connection while fetching the transcript.",
    retry: "Retry",
    unsavedTitle: "Unsaved changes",
    unsavedSwitchMessage: "Your session notes have unsaved edits. Discard them and switch?",
    discard: "Discard",
  },
  affektra: {
    title: "Affektra",
    runAgent: "Run agent",
    stop: "Stop",
    clearOutputs: "Clear outputs",
    emptyState: "Affektra will suggest insights and actions\nas the session progresses.",
    execute: "Execute",
    runAgain: "Run again",
    viewResult: "View result",
    // Card types
    insight: "Insight",
    newBoard: "New Board",
    newGoal: "New Goal",
    analysis: "Analysis",
    // Loading state
    analyzing: "Analyzing session...",
    analyzingSubtext: "Affektra is reviewing the transcript and generating insights.",
    // Feedback
    helpful: "Helpful",
    notHelpful: "Not helpful",
    // Confirm dialog
    clearOutputsTitle: "Clear Outputs",
    clearOutputsMessage: "Clear all Affektra outputs for this session?",
  },
  prepare: {
    title: "Session Preparation",
    subtitle: "Generate reminders based on your last sessions: open TODOs, promises, homework, and recurring themes.",
    generate: "Generate Preparation",
    regenerate: "Regenerate",
    lastSessions: "Last {{count}} sessions",
    loading: "Generating preparation...",
    empty: "Select a client and click Generate to prepare for your next session.",
    clientNotes: "Client Notes",
    generationFailed: "Generation failed",
    generationTimeout: "Generation timed out. Please try again.",
    noPreparation: "No preparation generated.",
  },
  prompts: {
    title: "Prompts",
    new: "New prompt",
    export: "Export prompts to JSON",
    searchPlaceholder: "Search name or description...",
    noMatch: "No prompts match your search.",
    loading: "Loading prompts...",
    masterPrompts: "Master Prompts",
    toolPrompts: "Tool Prompts",
    defaultDescription: "Short description.",
    defaultContent: "Write your prompt here. The session transcript and notes will be provided automatically.",
    editor: {
      title: "Edit Prompt",
      name: "Prompt name",
      kind: "Kind",
      description: "Description",
      descriptionPlaceholder: "Short description shown on the tile...",
      template: "Prompt Template",
      templateHint: "(runs on the currently selected session)",
      templatePlaceholder: "Write your prompt here...",
      result: "Result",
      run: "Run",
      save: "Save",
      useAsSummarizer: "Use as session summarizer",
      useAsSummarizerHint: "the Notes tab generates with this prompt",
    },
    summarizerBadge: "Summarizer",
    kinds: {
      summarize: "summarize",
      tool: "tool",
      trend: "trend",
      agent: "agent",
      prepare: "prepare",
    },
    extractor: {
      title: "Extractor settings",
      subtitle: "defines the measurement procedure for trend charts",
      source: "Data source",
      sourceAuto: "auto-detect (classified once on first run)",
      outputType: "Output",
      outputAuto: "auto",
      outputScore: "score — one number per session",
      outputTopics: "topics — themes scored 0–10",
      outputAirtime: "airtime — % of session per theme (embeddings, no LLM)",
      outputMarkers: "markers — client word rates per 1,000 words (pure code, no LLM)",
      ouraField: "Oura metric",
      threshold: "Similarity threshold (0–1)",
      taxonomy: "Tracked themes",
      taxonomyHint: "(one per line — leave empty for open-ended discovery)",
      taxonomyPlaceholder: "work stress\nsleep\nfamily conflict\nself-criticism",
      anchors: "Scale anchors",
      anchorsHint: "(what each score level means — anchored scales score consistently)",
      anchorsPlaceholder: "0 = theme absent\n5 = discussed in earnest\n10 = dominant theme with intense affect",
      versionNote: "Changing these fields creates a new extractor version: past sessions keep their existing scores, and new extractions are measured with the updated procedure.",
    },
  },
  assistant: {
    cards: {
      calendar: "Calendar",
      noAppointments: "No appointments in this range.",
      unknownPatient: "Unknown patient",
      similar_sessions: "Similar sessions",
      similar_moments: "Similar moments",
      noMatches: "No matches.",
      chair_audio: "Schema chair audio",
      audioExpires: "Audio links expire after an hour.",
    },
    approval: {
      title: "Allow this action?",
      allow: "Allow",
      deny: "Deny",
      unreadable: "The details of this action could not be read.",
      chairCost: "Generating voices uses TTS credits.",
      tools: {
        generate_chair_voices: "Generate chair audio",
      },
    },
    title: "Assistant",
    placeholder: "Ask for a formulation, interventions, homework ideas...",
    send: "Send",
    thinking: "Thinking...",
    error: "Something went wrong. Please try again.",
    newChat: "New Chat",
    newConversation: "New conversation",
    newConversationStarted: "New conversation started. Ask me anything for this session context.",
    deleteConversation: "Delete Conversation",
    deleteConversationMessage: "Delete this conversation? This removes it from the server.",
    showChatHistory: "Show chat history",
    showChatHistoryHint: "Show chat history",
    mainConversation: "Main conversation",
    conversationFallback: "Conversation",
    welcome:
      "You’re chatting with **Affektra**. Ask for formulations, interventions, homework ideas, or summaries.",
    working: "Working...",
    usingTool: "Using {{tool}}...",
    deleteFailed: "Failed to delete conversation",
    connectionLost: "Connection lost — the answer was interrupted. Please try again.",
    sendFailed: "Could not send the message. Check your connection and try again.",
    interrupted: "(interrupted)",
    errors: {
      internal: "The assistant hit an internal error. Please try again.",
      unavailable: "The assistant is temporarily unavailable. Please try again.",
      truncated: "The answer was cut short because it hit the length limit.",
      empty: "The assistant returned no answer. Please try again.",
      title: "Could not generate a conversation title.",
      unauthorized: "Session expired — please reload the page and sign in again.",
      silentDeath: "The assistant stopped responding without finishing. Please try again.",
    },
    effort: { quick: "Quick", balanced: "Balanced", deep: "Deep", hint: "Thinking depth" },
    starters: {
      similarSessions: "Show similar sessions",
      similarSessionsText: "Show me similar sessions to this.",
      similarSessionsSub: "Retrieve past similar cases",
      listSessions: "List all sessions",
      listSessionsText: "List all sessions for this client.",
      listSessionsSub: "Quick overview",
      schemaFit: "Schema assessment fit?",
      schemaFitText: "Does this session fit with the schema assessment?",
      schemaFitSub: "Mapping & consistency check",
      cbtPlan: "CBT plan",
      cbtPlanText: "Give me a CBT plan.",
      cbtPlanSub: "Goals → interventions → homework",
    },
  },
  journey: {
    title: "Journey",
    description: "Visualize client progress over time. Add charts to track themes, symptoms, or topics across sessions.",
  },
  digest: {
    eyebrow: "This week's reading",
    loading: "Reading the sessions…",
    error: "Couldn't compute the reading",
    headlineBoth: "{{falling}} is receding; {{rising}} is doing the lifting.",
    headlineRising: "{{rising}} is gaining ground across recent sessions.",
    headlineFalling: "{{falling}} is receding across recent sessions.",
    headlineWarning: "Rising instability around {{topic}} — a shift may be coming.",
    headlineGain: "Sudden gain on the {{instrument}} — something clicked.",
    headlineLoss: "Sudden setback on the {{instrument}} — worth a closer look this week.",
    headlineStable: "No reliable shifts this week — the picture is steady.",
    headlineNoData: "Not enough data yet — record sessions and run extractors to light this up.",
    sessionsMeta: "{{count}} sessions · last seen {{when}}",
    today: "today",
    daysAgo_one: "{{count}} day ago",
    daysAgo_other: "{{count}} days ago",
  },
  calendar: {
    title: "Calendar",
    today: "Today",
    prevWeek: "Previous week",
    nextWeek: "Next week",
    notifyClient: "Email the client about this change",
    invoicing: {
      label: "Session over — invoicing",
      configure: "Set up számlázz.hu…",
      create: "Create invoice",
      amount: "Amount (HUF)",
      created: "Invoice {{number}} created",
      failed: "Invoicing failed: {{msg}}",
      open: "Open in számlázz.hu",
      storno: "Storno",
      stornoConfirm: "Confirm storno",
      stornoed: "stornoed",
      stornoDone: "Storno {{number}} issued",
    },
    newAppointment: "New appointment",
    editAppointment: "Edit appointment",
    cancelAppointment: "Cancel appointment",
    patient: "Patient",
    date: "Date",
    time: "Time",
    duration: "Duration (min)",
    reminder: "Reminder",
    notes: "Notes",
    scopeThis: "This appointment only",
    scopeSeries: "All future appointments",
    leads: {
      0: "Off",
      60: "1 hour before",
      1440: "24 hours before",
      2880: "48 hours before",
    },
    recurrence: {
      label: "Repeats",
      none: "Does not repeat",
      weekly: "Weekly",
      biweekly: "Every two weeks",
      endBy: "Ends by (optional)",
    },
    gcal: {
      connect: "Connect Google Calendar",
      connected: "Google Calendar ✓",
      disconnect: "Disconnect Google Calendar",
      connectedToast: "Google Calendar connected",
      errorToast: "Google Calendar connection failed",
      failedCount: "{{count}} sync errors",
    },
    settings: {
      title: "Scheduling settings",
      workStart: "Workday starts",
      workEnd: "Workday ends",
      defaultDuration: "Default duration (min)",
      defaultReminder: "Default reminder (min before)",
      timezone: "Timezone",
      invoicingTitle: "Invoicing — számlázz.hu",
      agentKey: "Számla Agent key",
      agentKeySet: "saved — leave empty to keep",
      itemName: "Invoice line item",
      unitPrice: "Default session fee (HUF, gross)",
      vatKey: "VAT",
      paymentMethod: "Payment method",
      dueDays: "Payment due (days)",
      eInvoice: "Issue e-invoices (free via Agent)",
    },
    warnings: {
      overlap: "Overlaps another appointment",
      overlap_check_skipped_series: "Overlap check skipped for the series",
      email_skipped_no_address: "Patient has no email address — no notification sent",
      email_skipped_past: "Past appointment — no notification sent",
      email_send_failed: "Notification email failed to send",
    },
    errors: {
      loadFailed: "Failed to load the calendar: {{msg}}",
      moveFailed: "Failed to move the appointment: {{msg}}",
      saveFailed: "Failed to save: {{msg}}",
    },
  },
  goals: {
    title: "Homework",
    addGoal: "Add Goal",
    filterPlaceholder: "Filter goals...",
    noGoals: "No goals yet. Click “+ Add Goal” to create one.",
    noMatch: "No goals match your filter.",
    goalTitle: "Goal title...",
    goalDesc: "Describe the goal...",
    noTasks: "No tasks yet",
    addTask: "Add task",
    removeGoal: "Remove goal",
    editTask: "Edit Task",
    taskTitle: "Task title...",
    taskDesc: "Short description...",
    duplicate: "Duplicate",
    completedTasks: "Completed tasks",
    completed: "Completed",
    noCompleted: "No completed tasks yet. Drag a task onto the goal header to archive it.",
    goalDetails: "Goal details",
    loadingBoard: "Loading board...",
    dropToComplete: "Drop tasks here to mark as completed",
    clickToEdit: "Click to edit; drag onto goal header to archive",
    untitledTask: "Untitled task",
    addDescription: "Add a short description...",
    deleteCompletedTask: "Delete completed task",
    defaultBoardTitle: "Homework",
    defaultBoardDesc: "Therapy goals and tasks",
    saving: "Saving…",
    saved: "Saved",
    saveFailed: "Save failed.",
    retry: "Retry",
    conflictReloaded: "This board was changed elsewhere (client app or agent) — reloaded the latest version.",
    multipleBoards: "This client has {{count}} boards — showing the most recent one.",
    confirmDeleteGoal: "Delete “{{title}}” and its {{count}} tasks? Completion history will be lost.",
    completedThisWeek: "{{count}} this week",
    progress: "Progress",
    markComplete: "Mark “{{title}}” as complete",
    restoreTask: "Restore “{{title}}” to active",
    assignee: "Assigned to",
    assigneeUnset: "Not set",
    assigneeTherapist: "Therapist",
    assigneeClient: "Client (homework)",
    dueDate: "Due date",
    overdue: "Overdue",
    byClient: "by client",
    byTherapist: "by therapist",
  },
  stories: {
    title: "Stories",
    nowLast: "Last session",
    nowFact: "{{theme}}: {{early}} moments/session → now {{now}}",
    momentUnit: "moments",
    egyeb: "other",
    quieterThemes: "+{{count}} quieter themes",
    echoes: "Echoes — similar moments",
    openTranscript: "transcript →",
    coldStart:
      "Not enough material yet: {{sessions}}/{{minSessions}} sessions, {{moments}}/{{minMoments}} moments.",
    capped: "The map covers the most recent {{sessions}} sessions.",
    legendDot: "1 dot = 1 moment",
    legendDensity: "denser dots = more intense theme",
    legendEnvelope: "field = per-session volume",
    legendLatest: "latest session",
    legendEcho: "echo",
    legendContour: "contour = theme territory",
    legendOlder: "fainter dot = older",
    legendPre: "ghost = before time window",
    fullStory: "full story",
    play: "Play through time",
    windowStart: "Window start",
    windowEnd: "Window end",
    drift: "drift",
    loadError: "Failed to load the story map",
  },
  settings: {
    autoNote: {
      title: "Auto-draft progress notes",
      help: "After a session ends (web or phone), Affektra summarizes it and drafts a progress note in this format for you to review and sign.",
      off: "Off",
      notSet: "Not set yet: sessions are summarized, but no note is drafted until you pick a format.",
    },
    passkeys: {
      title: "Passkeys",
      hint: "Passkeys confirm it's you with your fingerprint or face. Save one to Google Password Manager to use it in the Affektra phone app too.",
      none: "No passkeys yet.",
      unnamed: "Passkey",
      add: "Add passkey",
      remove: "Remove",
      removeConfirm: "Remove passkey \"{{name}}\"? You will not be able to use it to sign in anymore.",
      removeFailed: "Could not remove the passkey.",
      loadFailed: "Could not load your passkeys.",
    },
    toolbar: "Toolbar",
    toolbarHint: "Choose which tools are visible in the sidebar. At least 2 must remain visible.",
    hideFromToolbar: "Hide from toolbar",
    dangerZone: "Danger zone",
    deleteAccount: "Delete my account",
    deleteAccountWarning:
      "Permanently deletes your account and ALL patient data — every session, transcript, analysis, assessment and note. This cannot be undone.",
    deleteAccountType: "Type DELETE to confirm",
    deleteAccountConfirmPrompt: "This will permanently erase your entire practice. Are you absolutely sure?",
    deleteAccountFailed: "Account deletion failed — please try again.",
    deleting: "Deleting…",
    subscription: "Subscription",
    subPlanTrial: "Free trial (full features)",
    subPlanAlap: "Basic — 20 sessions / month",
    subPlanPro: "Pro — unlimited",
    planNameAlap: "Basic",
    planNamePro: "Pro",
    subPlanNone: "No active plan",
    subTrialEnds: "Trial ends",
    subRenews: "Renews",
    subCancelAtEnd: "Cancels at period end",
    subUpgradeAlap: "Subscribe to Basic",
    subUpgradePro: "Subscribe to Pro",
    subManage: "Manage billing",
    subCheckoutFailed: "Could not start checkout — please try again.",
    subAlreadySubscribed: "You already have an active subscription — refreshing.",
    subPortalFailed: "Could not open the billing portal — please try again.",
    subLoadFailed: "Could not load your subscription — please reload.",
    subResumePrompt: "You picked the {{tier}} plan — subscribe below.",
    billingSuccess: "Payment received — your subscription is now active.",
    billingCancelled: "Checkout was cancelled — no charge was made.",
    planRequired: "This feature needs an active plan — open Billing from the user menu.",
    sessionLimit: "You've reached this month's session limit on the Basic plan.",
  },
  guide: {
    title: "How to use Affektra",
    subtitle: "Select what you'd like to do and follow the step-by-step instructions.",
    dropdownLabel: "What would you like to do?",
    tip: "Tip",
    whereToFind: "Where to find it",
    groupBefore: "Before Session",
    groupDuring: "During Session",
    groupAfter: "After Session",
    // Core concept diagram
    conceptTitle: "How Affektra Works",
    conceptSubtitle: "",
    conceptPrompts: "Your Prompts",
    conceptPromptsHint: "Therapy & note templates",
    conceptPromptsDesc: "Create prompts that describe therapies and guide note-taking",
    conceptSession: "Session Data",
    conceptSessionHint: "Transcripts & notes",
    conceptSessionDesc: "Record sessions to capture transcripts, summaries & notes",
    conceptAI: "Affektra",
    conceptAIHint: "AI + learns from feedback",
    conceptAIDesc: "Runs your prompts on session data to generate insights",
    conceptResults: "Results",
    conceptResultsHint: "Insights & actions",
    conceptResultsDesc: "Get summaries, goals, analysis & actionable suggestions",
    conceptKeyInsight: "Note",
    conceptKeyInsightText: "The prompts you create shape what Affektra looks for. Check the Prompts tab to customize or add your own.",
    conceptStep1: "Record sessions to capture transcripts and notes",
    conceptStep2: "Create prompts that describe therapies and guide documentation",
    conceptStep3: "Run prompts on sessions to generate insights, summaries, and goals",
  },
  charts: {
    loading: "Loading...",
    errorLoading: "Error loading data",
    chartType: "Chart Type",
    extractors: "Extractors (select multiple)",
    noExtractors: "No extractors available",
    runExtractor: "Run extractor to generate tags",
    extract: "Extract",
    confirmDelete: "Are you sure you want to delete \"{{title}}\"?",
    correlation: "Correlation",
    correlationTitle: "Topic-Mood Correlation",
    noCorrelationData: "Select both a topic extractor and a score extractor to see correlations.",
    topicShift: "Trend",
    noTopicShiftData: "Need topic data across sessions to estimate trends.",
    cooccurrence: "Co-occurrence",
    noCooccurrenceData: "Need a topic extractor with 2+ themes to show co-occurrence.",
    volatility: "Instability",
    noVolatilityData: "Need at least 3 sessions with topic data to measure instability.",
    notEnoughSessions: "Needs at least {{need}} sessions for a defensible statistic — this client has {{have}} usable so far.",
    compositionalExcluded: "This extractor produces percentages that sum to 100%, which makes correlations spurious by construction. Use a 0–10 scale (independent topics) extractor instead.",
    noAssessmentData: "No assessment scores yet. Upload assessments and select an assessment extractor.",
    noOuraData: "No Oura data in this date range — the client may not have connected their ring.",
    noLeadlagData: "Select a topic extractor; lead-lag needs session themes plus daily Oura data.",
    noEarlywarningData: "Early-warning signals need at least 9 sessions per series (5-session rolling window).",
    noNetworkData: "Select a topic extractor with 2+ themes — the network needs multiple series over 10+ sessions.",
    networkNoEdges: "Tested {{tested}} directed links — none survives FDR control yet. More sessions sharpen this map.",
    sessions: "sessions",
    sessionsLabel: "Sessions",
    sessionsAll: "All sessions",
    renderLabel: "Display",
    renderAuto: "Auto",
    renderRows: "Split into rows",
    renderOverlay: "Single panel",
    hintBar: "Select 1 or more extractors",
    hintCorrelation: "Select a topic extractor + a score extractor (2)",
    hintTopicShift: "Select a topic extractor (1)",
    hintCooccurrence: "Select a topic extractor (1)",
    hintVolatility: "Select a topic extractor (1)",
    hintAssessment: "Select an assessment instrument",
    hintLeadlag: "Select a topic extractor and an Oura metric",
    hintDynamics: "No selection needed — computed from session embeddings",
    hintEarlywarning: "Select topic or score extractors (needs 9+ sessions)",
    hintNetwork: "Select a topic extractor with several themes (needs 10+ sessions)",
    hintPhysio: "No selection needed — reads daily Oura data directly",
    descBar: "Stacked bar chart showing topic distribution over time. Each bar represents one session, broken down by the percentage of time spent on each life domain. Use this to spot which areas dominate the conversation and how the balance shifts across sessions — for example, whether work-related talk is gradually giving way to relationship themes.",
    descCorrelation: "Spearman rank correlation between session-to-session CHANGES in each topic and changes in the client's score — detrended so two values that merely drift together over treatment don't fake a link. p-values come from permutation tests with FDR adjustment across all pairs; non-significant bars are gray. Needs 8+ shared sessions. Read it as: 'when this theme rose between sessions, the score tended to rise/fall too.'",
    descTopicShift: "Robust trend per topic: the Theil-Sen slope (median of pairwise slopes, in points per week) with a Mann-Kendall significance test, FDR-adjusted across topics. Positive bars grow over treatment, negative bars fade; gray bars are not statistically distinguishable from no trend. Answers: 'What is reliably increasing or decreasing across therapy?'",
    descCooccurrence: "Heatmap of detrended Spearman correlations between every pair of themes across sessions, with permutation p-values and FDR control — so a 'cluster' shown here is statistically defensible, not an artifact of testing many pairs. Non-significant cells are muted with the value in parentheses. Needs 8+ sessions per pair.",
    descVolatility: "Session-to-session instability per topic, measured as RMSSD (root mean square of successive differences) — the standard instability measure in affect research. Unlike plain standard deviation, a steady improving trend does NOT count as instability; only real back-and-forth swings do. High bars mean the theme spikes and vanishes between consecutive sessions.",
    descAssessment: "Validated assessment scores (e.g. PHQ-9, GAD-7) plotted over time with clinical cutoff bands, so reliable change is visible at a glance.",
    descLeadlag: "Cross-correlation between a session theme and daily Oura physiology at lags of ±N days — does the body lead the mind, or follow it?",
    descDynamics: "Embedding-derived session dynamics: novelty (how far each session moves from the previous one) and theme entropy (focused vs. scattered sessions). Computed without any LLM calls.",
    descEarlywarning: "Critical-slowing-down detector from dynamic-systems research: rising lag-1 autocorrelation AND rising variance in a 5-session rolling window often precede a sudden clinical shift — breakthrough or relapse. Each series is a dot; the top-right quadrant (both trends rising, Mann-Kendall p<.10) is the destabilization zone, marked red. A watch flag, not a diagnosis.",
    descNetwork: "Temporal theme network: an arrow from A to B means changes in A this session predict changes in B next session (lagged Spearman, detrended, FDR-controlled — only surviving edges are drawn). Green = drives up, red = drives down. Node size is out-strength: the biggest node is the best candidate intervention target, the theme that moves the rest.",
    descPhysio: "Daily Oura trajectories (sleep, readiness, HRV, pulse) — a raw daily dot cloud plus a 7-day rolling mean per metric, with session dates marked as faint vertical lines. Each row's trend (Theil-Sen slope per year, Mann-Kendall p) is computed on ISO-week means to avoid overstating significance from day-to-day autocorrelation.",
    typeBar: "Timeline",
    typeAssessment: "Assessments",
    typeLeadlag: "Lead-Lag (Oura)",
    typeDynamics: "Session Dynamics",
    typeEarlywarning: "Early Warning",
    typeNetwork: "Theme Network",
    typePhysio: "Body trajectory",
    addChart: "Add Chart",
    filterPlaceholder: "Filter charts...",
    loadingCharts: "Loading charts...",
    createNew: "Create New Chart",
    titleLabel: "Title",
    descriptionLabel: "Description",
    descriptionPlaceholder: "What does this chart measure?",
    extractorsLabel: "Extractors",
    lastN: "Last {{count}}",
    creating: "Creating...",
    create: "Create Chart",
    createFailed: "Failed to create chart: {{message}}",
    updateFailed: "Failed to update chart: {{message}}",
    deleteFailed: "Failed to delete chart: {{message}}",
  },
  errors: {
    noAuthToken: "No auth token - user not authenticated.",
    noMediaStream: "No media stream",
    getUserMediaNotSupported: "getUserMedia not supported in this browser.",
    missingSessionId: "Missing session id",
    failedToCreateSession: "Failed to create session before recording.",
  },
  clientSelector: {
    label: "Client:",
    loading: "Loading\u2026",
    failedToLoad: "Failed to load",
    noClients: "No clients",
    error: "Failed to load clients",
  },
  sessionSelector: {
    label: "Session:",
    selectPatientFirst: "Select a client first",
    currentSession: "Current Session",
    errorLoading: "Error loading sessions",
    failedToLoad: "Failed to load sessions",
  },
  patients: {
    title: "Clients",
    searchPlaceholder: "Search name, email, or phone...",
    reload: "Reload",
    new: "New",
    name: "Name",
    email: "Email",
    phone: "Phone",
    dob: "DOB",
    actions: "Actions",
    noMatch: "No clients match your search.",
    editPatient: "Edit Client",
    newPatient: "New Client",
    notes: "Notes",
    dobPlaceholder: "YYYY-MM-DD or free text",
    saving: "Saving...",
    saveChanges: "Save changes",
    billingTitle: "Invoicing data",
    billingName: "Billing name (if different)",
    billingZip: "ZIP",
    billingCity: "City",
    billingAddress: "Street address",
    billingTaxNumber: "Tax number (companies)",
    deletePatient: "Delete Client",
    confirmDelete: "Delete client {{name}}? This cannot be undone.",
    creating: "Creating...",
    create: "Create",
    load: "Load",
    connectorWipTag: "wip",
    connectorWipTooltip: "In the works — not connectable yet",
    connectors: "Connectors",
    inviteToApp: "Invite to App",
    inviteSending: "Sending...",
    inviteSent: "Invite sent",
    inviteActive: "App Active",
    inviteDisabled: "App Disabled",
    revokeAccess: "Revoke",
    inviteSuccess: "Invite sent to {{email}}",
    inviteError: "Failed to send invite",
    revokeSuccess: "App access revoked",
    revokeError: "Failed to revoke access",
    noEmail: "Add an email address first",
  },
  consent: {
    badgeFull: "Consent on file",
    badgePartial: "Partial consent",
    badgeNone: "No consent",
    statusUnavailable: "Consent status unavailable",
    loading: "Loading...",
    dialogTitle: "Data processing consent",
    policyVersion: "Policy version: {{version}}",
    close: "Close",
    thePatient: "the client",
    purposeRecording: "Session recording",
    purposeRecordingDesc: "Audio recording and transcription of therapy sessions",
    purposeAiAnalysis: "AI analysis",
    purposeAiAnalysisDesc: "AI-generated summaries, tags, trends and similar stories",
    purposeWearables: "Wearable data",
    purposeWearablesDesc: "Oura Ring health metrics (sleep, readiness, HRV)",
    statusGranted: "Granted",
    statusNotGranted: "Not granted",
    attestGrant: "Record consent",
    attestRevoke: "Record withdrawal",
    confirmGrantTitle: "Confirm attestation",
    confirmRevokeTitle: "Confirm withdrawal",
    confirmGrantText:
      "You are recording that {{name}} has consented to: {{purpose}}. This creates a permanent entry in the consent log.",
    confirmRevokeText:
      "You are recording that {{name}} has withdrawn consent for: {{purpose}}. Future processing for this purpose stops; existing data is kept until erasure is requested.",
    confirmCheckboxGrant:
      "I confirm the client has given explicit consent (on paper or verbally), and I retain the signed consent form.",
    confirmCheckboxRevoke:
      "I confirm the client has withdrawn this consent (on paper or verbally).",
    confirmSubmit: "Confirm",
    confirmSubmitting: "Saving...",
    cancel: "Cancel",
    attestSuccess: "Consent record saved",
    attestError: "Failed to save consent record",
    loadError: "Failed to load consent status",
    retry: "Retry",
    historyTitle: "History",
    historyEmpty: "No consent events recorded yet.",
    channelMobileApp: "Client app",
    channelDashboard: "Therapist attestation",
    actionGranted: "granted",
    actionRevoked: "revoked",
    recordingWarning: "No recording consent on file",
    recordingWarningTooltip:
      "There is no recorded consent for session recording for this client. Recording is not blocked yet, but consent should be captured before the session.",
  },
  oura: {
    connect: "Connect Oura Ring",
    connected: "Oura Connected",
    disconnect: "Disconnect",
    disconnected: "Oura Ring disconnected.",
    connectError: "Failed to create invite",
    disconnectError: "Failed to disconnect",
    inviteTitle: "Share Oura Invite Link",
    inviteHint: "Send this link to your client. They will authorize Oura Ring data sharing.",
    copyLink: "Copy link",
    copied: "Copied!",
    extractorName: "Oura Ring",
    consentTitle: "Connect Your Oura Ring",
    consentBody: "Your therapist invited you to share your Oura Ring data (sleep, readiness, HRV).",
    connectButton: "Connect with Oura",
    successTitle: "Oura Ring Connected",
    successBody: "Your data is now shared with your therapist. You can close this page.",
    errorTitle: "Connection Failed",
    sleepScore: "Sleep Score",
    readiness: "Readiness",
    hrvBalance: "HRV Balance",
  },
  notesReview: {
    title: "Notes to review",
    loadFailed: "Couldn't load note drafts",
    unknownPatient: "Unknown patient",
  },
  login: {
    title: "Affektra",
    subtitle: "Sign in to your account",
    email: "Email",
    emailPlaceholder: "you@example.com",
    password: "Password",
    passwordPlaceholder: "Enter your password",
    rememberDevice: "Remember this device for 30 days",
    signingIn: "Signing in...",
    signIn: "Sign in",
    forgotPassword: "Forgot password?",
    forgotPasswordSubtitle: "Enter your email and we'll send you a reset code.",
    sendCode: "Send code",
    sendingCode: "Sending code...",
    resetCode: "Verification code",
    resetCodePlaceholder: "Enter the 6-digit code",
    newPassword: "New password",
    newPasswordPlaceholder: "Enter your new password",
    resetPassword: "Reset password",
    resettingPassword: "Resetting password...",
    resetSuccess: "Password reset successful",
    resetSuccessSubtitle: "You can now sign in with your new password.",
    backToLogin: "Back to login",
    emailCodeTitle: "Check your email",
    emailCodeSubtitle: "We sent a 6-digit sign-in code to {{email}}. Next you'll set up a passkey, so from then on your fingerprint or face is enough.",
    emailCodeVerify: "Verify",
  },
  experiential: {
    suggest: {
      title: "Prefill from sessions",
      hint: "Pick up to {{n}} sessions — AI proposes the active modes and what each says, in the client's words.",
      run: "Suggest chairs",
      running: "Reading the sessions…",
      replaceConfirm: "Replace the current chairs and lines with the suggestions?",
      failed: "Couldn't suggest chairs",
      consent: "This patient hasn't consented to AI analysis.",
      sessionsFailed: "Couldn't load sessions",
      noSessions: "No sessions for this patient yet.",
    },
    title: "Experiential",
    chairTitle: "Schema Chair",
    rescriptTitle: "Imagery Rescripting",
    addChair: "Add chair",
    removeChair: "Remove",
    text: "What this mode says",
    voice: "Voice",
    mode: "Mode",
    generate: "Generate voices",
    play: "Play",
    stop: "Stop",
    loop: "Loop",
    amplify: "Amplify",
    scene: "Remembered scene",
    rescript: "Rescription",
    generateImages: "Generate images",
    before: "Before",
    after: "After",
    present: "Present to patient",
    policyDeclined: "The image model declined this description — try wording it more symbolically.",
    genError: "Generation failed. Please try again.",
    modes: {
      punitive_parent: "Punitive Parent",
      demanding_parent: "Demanding Parent",
      vulnerable_child: "Vulnerable Child",
      angry_child: "Angry Child",
      impulsive_child: "Impulsive Child",
      happy_child: "Happy/Contented Child",
      detached_protector: "Detached Protector",
      compliant_surrenderer: "Compliant Surrenderer",
      healthy_adult: "Healthy Adult",
      custom: "Custom",
    },
    backToLibrary: "All techniques",
    searchTechniques: "Search techniques…",
    noTechniqueMatch: "No technique matches your search.",
    groups: {
      voice: "Voice",
      image: "Image",
      combined: "Voice + image",
    },
    techniques: {
      schema_chair: "Schema Chair",
      spatial_modes: "Spatial Modes",
      empty_chair: "Empty Chair",
      parts_dialogue: "Parts Dialogue (IFS)",
      bus_passengers: "Passengers on the Bus",
      compassionate_letter: "Compassionate Letter",
      needed_words: "The Needed Words",
      defusion_voice: "Defusion Voice",
      loop_tape: "The Loop Tape",
      tape_forward: "Playing the Tape Forward",
      urge_surfing: "Urge Surfing",
      couples_cycle: "The Cycle, Replayed",
      brain_cast: "The Brain Cast",
      playback_theatre: "Playback Theatre",
      tale_story: "Therapeutic Tale",
      metaphor_induction: "Metaphor Induction",
      sleep_story: "Sleep Story",
      miracle_question: "The Miracle Question",
      worry_monster: "The Worry Monster",
      brave_rehearsal: "Brave Rehearsal",
      nightmare_rescript: "Nightmare Rescripting",
      pendulation: "Pendulation",
      imagery_rescript: "Imagery Rescripting",
      felt_sense: "Felt-Sense Image",
      safe_container: "Safe Container",
      safe_place: "Safe Place",
      metaphor_deck: "Metaphor Deck",
      distant_screen: "The Distant Screen",
      compassionate_other: "Compassionate Other",
    },
    techniqueHints: {
      schema_chair: "Schema modes speak together — mix them live",
      spatial_modes: "Each mode speaks from its own place in the room (headphones)",
      empty_chair: "A dialogue with someone who isn't in the room",
      parts_dialogue: "Inner parts and Self, speaking in turn",
      bus_passengers: "The thoughts clamor; the client keeps driving",
      compassionate_letter: "The client's kind words, read back to them",
      needed_words: "The words they needed to hear, finally spoken",
      defusion_voice: "The sticky thought re-spoken — slow, fast, far away",
      loop_tape: "The obsessional thought on repeat, until it loses its grip",
      tape_forward: "The urge played past the first drink — then the other ending",
      urge_surfing: "Ride the craving as a wave: swell, crest, pass",
      couples_cycle: "The pursue–withdraw dance, heard from the outside",
      brain_cast: "Amygdala and cortex explain the panic, as characters",
      playback_theatre: "The client's moment, performed back by the voices",
      tale_story: "A therapeutic tale, narrated chapter by chapter",
      metaphor_induction: "A slow personalized journey, Ericksonian style",
      sleep_story: "Their safe place, woven into a wind-down narration",
      miracle_question: "The morning after the miracle, seen and heard",
      worry_monster: "The worry as a friendly creature a child can out-argue",
      brave_rehearsal: "The feared moment, rehearsed with a coping voice",
      nightmare_rescript: "The nightmare rewritten and rehearsed (IRT)",
      pendulation: "Swing between the resource and the stressor, paced",
      imagery_rescript: "A remembered scene and its rescription, as images",
      felt_sense: "Refine an image until it fits the felt sense",
      safe_container: "A container strong enough to hold it, rendered",
      safe_place: "The client's safe place, rendered from their words",
      metaphor_deck: "Symbolic cards from a theme — pick what resonates",
      distant_screen: "Watch the scene recede: near, distant, far away",
      compassionate_other: "An ideal compassionate being, seen and heard",
    },
    roles: {
      self: "Self",
      other: "The Other",
      the_tape: "The Tape",
      the_alternative: "The Alternative",
      pursuer: "The Pursuer",
      withdrawer: "The Withdrawer",
      amygdala: "Amygdala",
      cortex: "Cortex",
      breath: "The Breath",
      narrator: "Narrator",
      voice: "A Voice",
      character: "Character",
      ifs_self: "Self",
      manager: "Manager",
      firefighter: "Firefighter",
      exile: "Exile",
      critic: "The Critic",
      worry: "Worry",
      fear: "Fear",
      shame: "Shame",
      anger: "Anger",
      custom: "Custom",
    },
    chairLabel: "Chair",
    part: "Part",
    passenger: "Passenger",
    addTurn: "Add turn",
    addPassenger: "Add passenger",
    readBy: "Read by",
    letterText: "The letter, in the client's own words",
    neededText: "The words they needed to hear",
    feltSenseDesc: "Describe the felt sense",
    refineLabel: "What would make it fit better?",
    refineHint: 'e.g. "warmer", "heavier", "more like dusk than night"',
    refine: "Refine",
    attempt: "Attempt",
    containerDesc: "Describe the container",
    deckTheme: "Theme",
    deckCount: "Cards",
    generateDeck: "Generate deck",
    card: "Card",
    distScene: "The scene",
    generateSeries: "Generate series",
    distSteps: {
      near: "Close",
      mid: "Distant",
      far: "Far away",
    },
    otherDesc: "Describe the compassionate being",
    otherWords: "What they say",
    safePlaceDesc: "Describe the safe place",
    take: "Take",
    partner: "Partner",
    character: "Character",
    addChapter: "Add chapter",
    loopTapeText: "The obsessional thought, exactly as it comes",
    narrationText: "The narration",
    inductionText: "The induction script",
    sleepStoryText: "The wind-down story (weave in their safe place)",
    miracleDesc: "The morning after the miracle — what do they see?",
    miracleWords: "How they describe that morning",
    monsterDesc: "Describe the worry as a creature",
    monsterWords: "What the worry monster says",
    braveDesc: "The feared moment, as a scene",
    braveWords: "The coping script, in their corner",
    nightmareScene: "The recurring nightmare",
    nightmareRescript: "The rewritten version",
    playNightmare: "Play nightmare",
    rehearseRescript: "Rehearse the rescript",
    nightmareLabel: "The nightmare",
    rescriptLabel: "Rewritten",
    pendulationStressor: "The heavy thing (stressor)",
    pendulationResource: "The resource (where it's okay)",
    pendulationNarration: "The pacing narration",
    pendulateToStressor: "Glance at the heavy thing",
    pendulateToResource: "Back to the resource",
    spatialHint: "Best with headphones — each voice speaks from its own place in the room.",
    defusionText: "The sticky thought",
    defusionVariants: {
      plain: "As spoken",
      slow: "Slowed down",
      fast: "Sped up",
      far: "Far away",
    },
  },
  assessments: {
    sendQuestionnaire: "Send questionnaire",
    send: "Send",
    sending: "Sending…",
    items: "items",
    statusSent: "Sent",
    sentAt: "Sent",
    completedAt: "Completed",
    score: "Score",
    awaitingResponse: "Waiting for the patient to fill this out.",
    definitionsUnavailable: "Instrument definitions could not be loaded.",
    upload: "Upload",
    chooseFile: "Choose file",
    uploading: "Uploading…",
    uploaded: "Uploaded",
    uploadedAt: "Uploaded",
    noUploads: "No uploads yet.",
    noUploadsChip: "No uploads yet",
    filesCount_one: "{{count}} file",
    filesCount_other: "{{count}} files",
    noticeUploaded: "Uploaded.",
    noticeSaved: "Saved.",
    noticeDeleted: "Deleted.",
    noticeParsed: "Parsed.",
    loadFailed: "Failed to load assessments",
    uploadFailed: "Upload failed",
    openEditorFailed: "Failed to open editor",
    saveFailed: "Save failed",
    parseFailed: "Parse failed",
    parseRefreshFailed: "Parse failed, refresh also failed",
    deleteFailed: "Delete failed",
    deleteTitle: "Delete Assessment",
    deleteConfirm: "Delete \"{{name}}\"?",
    statusParsed: "Parsed",
    statusParsing: "Parsing",
    statusError: "Error",
    statusNotParsed: "Not parsed",
    parsing: "Parsing…",
    editParser: "Edit parser",
    parserPrompt: "Parser prompt",
    parserPromptPlaceholder: "Enter parser prompt…",
    result: "Result",
    noResultYet: "No result yet. Click Run.",
    parseModel: "model",
    parsedAt: "parsed",
    keysLabel: "keys",
    keysMissing: "keys missing (tp_pk/sk)",
    reviewTemplateNotFound: "Could not open this questionnaire for review — please try again.",
    sendBuiltinGroup: "Built-in instruments",
    sendCustomGroup: "Your questionnaires",
    sendNoTemplates: "No ready questionnaires yet — upload and review one above.",
    channelLabel: "Delivery channel",
    sendChannelApp: "App",
    sendChannelEmail: "Email",
    sendGateBuiltinAppOnly: "Built-in questionnaires are app-only.",
    sendGateNoClientAccount: "Invite this client to the app first.",
    sendGateNoEmail: "No email on file for this client.",
    sendGateLoading: "Checking client account…",
    attachOriginal: "Attach original file",
    channelApp: "App",
    channelEmail: "Email",
    statusCompletedManually: "Completed manually",
    completedManuallyNote: "Completed manually — no digital responses.",
    markCompleted: "Mark completed",
    markCompletedFailed: "Could not mark this questionnaire as completed.",
    noticeMarkedCompleted: "Marked as completed.",
    cancelSend: "Cancel send",
    cancelSendTitle: "Cancel questionnaire send?",
    cancelSendConfirm: "Cancel the send of \"{{name}}\"? The client will no longer be able to submit answers.",
    cancelSendConfirmLabel: "Cancel send",
    cancelSendFailed: "Could not cancel this send.",
    noticeCancelled: "Send cancelled.",
  },
  qshelf: {
    title: "Your questionnaires",
    hint: "Upload a PDF, text, Markdown, Excel, or Word file with your own questionnaire — Affektra will parse it into items you can review and send.",
    uploadButton: "Upload questionnaire",
    uploading: "Uploading…",
    uploadingPct: "Uploading… {{pct}}%",
    loading: "Loading your questionnaires…",
    empty: "No questionnaires uploaded yet.",
    tooLarge: "That file is larger than 7MB — please upload a smaller file.",
    statusParsing: "Parsing…",
    statusNeedsReview: "Needs review",
    statusReady: "Ready",
    statusParseFailed: "Parse failed",
    review: "Review",
    send: "Send",
    loadFailed: "Failed to load your questionnaires.",
    uploadFailed: "Upload failed.",
    parseFailed: "Parsing failed.",
    deleteFailed: "Delete failed.",
    deleteTitle: "Delete questionnaire",
    deleteConfirm: "Delete \"{{name}}\"? This cannot be undone.",
  },
  qreview: {
    title: "Review questionnaire",
    nameLabel: "Name",
    timeframeLabel: "Timeframe (optional)",
    timeframePlaceholder: "e.g. Over the last 2 weeks",
    langLabel: "Language",
    langEn: "English",
    langHu: "Hungarian",
    itemsTitle: "Items",
    itemTextPlaceholder: "Item text",
    addItem: "Add item",
    moveUp: "Move item up",
    moveDown: "Move item down",
    scaleTitle: "Scale",
    scaleValuePlaceholder: "Value",
    scaleLabelPlaceholder: "Label",
    addScaleStep: "Add step",
    saving: "Saving…",
    saveFailed: "Save failed.",
    errorNameRequired: "Name is required.",
    errorNameTooLong: "Name is too long (max {{max}} characters).",
    errorTimeframeTooLong: "Timeframe is too long (max {{max}} characters).",
    errorItemsRequired: "At least one item is required.",
    errorItemsTooMany: "Too many items ({{count}}; max {{max}}).",
    errorItemTextRequired: "Item text is required.",
    errorItemTextTooLong: "Item text is too long (max {{max}} characters).",
    errorItemIdDuplicate: "This item has a duplicate id — remove and re-add it.",
    errorScaleStepsMin: "The scale needs at least 2 steps.",
    errorScaleStepsTooMany: "Too many scale steps ({{count}}; max {{max}}).",
    errorScaleValueInvalid: "Value must be a whole number.",
    errorScaleValueDuplicate: "Each scale value must be unique.",
    errorScaleLabelRequired: "Label is required.",
    errorScaleLabelTooLong: "Label is too long (max {{max}} characters).",
  },
  toolkit: {
    searchPlaceholder: "Search techniques…",
    filterSource: "Source",
    filterModality: "Input type",
    filterAll: "All",
    filterMine: "Mine",
    filterLibrary: "Library",
    filterAnyInput: "Any input",
    filterTranscript: "Transcript",
    filterAudio: "Audio",
    filterVision: "Vision",
    badgeMine: "Mine",
    badgeLibrary: "Library",
    badgeWip: "In the works",
    modality: {
      transcript: "Runs on session",
      audio: "Voice tool",
      vision: "Image tool",
      both: "Voice + image tool",
    },
    noMatch: "No techniques match.",
    loading: "Loading techniques…",
    retry: "Retry",
    back: "Back to toolkit",
    run: "Run",
    running: "Running…",
    runTimeout: "Timed out waiting for the result.",
    sessionsLabel: "Sessions",
    runCount: "Run · {{count}} sessions",
    noSummaryFor: "These sessions have no summary yet: {{dates}}. Summarize them first.",
    selectClientFirst: "Select a client to use this technique.",
    noSessions: "This client has no saved sessions yet.",
    copy: "Copy",
    copied: "Copied to clipboard",
    seeExample: "See example",
    comingSoon: "This technique is still in the works — the example below shows what it will produce.",
  },
  progressNotes: {
    generate: "Generate note",
    generating: "Generating…",
    language: "Language",
    statusDraft: "Draft",
    statusSigned: "Signed",
    draftBanner: "AI draft — review before signing.",
    save: "Save",
    sign: "Sign",
    signConfirm: "Signing locks this note. Continue?",
    signedAt: "Signed",
    regenerate: "Regenerate",
    regenerateConfirm: "Regenerating discards your edits. Continue?",
    delete: "Delete",
    deleteDraftConfirm: "This deletes the note draft. Continue?",
    deleteSignedConfirm: "This deletes a signed clinical note. Continue?",
    copy: "Copy",
    copied: "Copied",
  },
  publicFill: {
    loading: "Loading questionnaire…",
    submit: "Submit answers",
    submitting: "Submitting…",
    requiredField: "Please answer this question.",
    requiredAll: "Please answer every question before submitting.",
    thanksTitle: "Thank you",
    thanksBody: "Your answers have been submitted. You can close this page.",
    errorNotFoundTitle: "Link not found",
    errorNotFoundBody: "This questionnaire link is invalid. Please ask your therapist for a new one.",
    errorExpiredTitle: "Link expired",
    errorExpiredBody: "This questionnaire link has expired. Please ask your therapist to send a new one.",
    errorUsedTitle: "Already submitted",
    errorUsedBody: "This questionnaire has already been completed. There is nothing more to do.",
    errorGenericTitle: "Something went wrong",
    errorGenericBody: "We couldn't load this questionnaire. Please try again, or ask your therapist for a new link.",
  },
};
export default en;
